// hooks/useNotifications.js
//
// SINGLETON WebSocket manager.
//
// Previously this hook created a NEW WebSocket in every component that used
// it (Header, NotificationsDropdown, NotificationsPage = up to 3 sockets per
// page), and its reconnect loop reused the token captured at mount — so once
// the access token rotated, reconnects retried a dead token forever.
//
// Now: one shared connection at module level, reference-counted by hook
// consumers. Every (re)connect attempt gets a FRESH token via
// ensureFreshToken(), which refreshes it first if it's expired.
import { useEffect, useCallback, useSyncExternalStore } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../features/notifications/notificationSlice';
import { ensureFreshToken } from '../api/axiosInstance';

const WS_BASE = 'wss://server.survill.com/ws/notifications/';
const MAX_RECONNECT_ATTEMPTS = 8;

// ── Module-level shared state (one per tab, not one per component) ──────────
const manager = {
  ws: null,
  refs: 0,                 // how many mounted components are using the hook
  attempts: 0,
  reconnectTimer: null,
  dispatch: null,          // all consumers share one Redux store
  connecting: false,
  statusListeners: new Set(),

  isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  },

  notifyStatus() {
    this.statusListeners.forEach((fn) => fn());
  },

  async connect() {
    if (this.connecting || this.isOpen() || this.refs === 0) return;
    this.connecting = true;

    try {
      // ALWAYS dial with a fresh token — refreshes first if needed.
      const token = await ensureFreshToken();
      if (!token || this.refs === 0) return;

      const ws = new WebSocket(`${WS_BASE}?token=${token}`);
      this.ws = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        this.attempts = 0;
        this.notifyStatus();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connection_established') {
            console.log('Notification system connected:', data.message);
          } else {
            this.dispatch?.(addNotification(data));
            playNotificationSound();
            showBrowserNotification(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.ws = null;
        this.notifyStatus();
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.scheduleReconnect();
    } finally {
      this.connecting = false;
    }
  },

  scheduleReconnect() {
    if (this.refs === 0) return; // nobody is listening — stay closed
    if (this.attempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('Max WebSocket reconnect attempts reached');
      return;
    }
    const delay = Math.min(1000 * Math.pow(2, this.attempts), 30000);
    console.log(`Reconnecting in ${delay}ms... (attempt ${this.attempts + 1})`);
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.attempts++;
      this.connect();
    }, delay);
  },

  acquire(dispatch) {
    this.refs++;
    this.dispatch = dispatch;
    this.attempts = 0;
    this.connect();
  },

  release() {
    this.refs = Math.max(0, this.refs - 1);
    if (this.refs === 0) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      if (this.ws) {
        this.ws.onclose = null; // don't trigger reconnect on intentional close
        this.ws.close();
        this.ws = null;
      }
      this.notifyStatus();
    }
  },

  send(payload) {
    if (this.isOpen()) {
      this.ws.send(JSON.stringify(payload));
    }
  },
};

// Reconnect promptly when the tab comes back to foreground or network returns.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (manager.refs > 0 && !manager.isOpen()) {
      manager.attempts = 0;
      manager.connect();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && manager.refs > 0 && !manager.isOpen()) {
      manager.attempts = 0;
      manager.connect();
    }
  });
}

// ── Hook (same public API as before — no changes needed in consumers) ───────
const useNotifications = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!token) return undefined;
    manager.acquire(dispatch);
    return () => manager.release();
    // Intentionally NOT keyed on `token`: rotation is handled by
    // ensureFreshToken() at connect time, so we don't tear the socket down
    // on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!token, dispatch]);

  const markAsRead = useCallback((notificationId) => {
    manager.send({ type: 'mark_read', notification_id: notificationId });
  }, []);

  const markAllAsRead = useCallback(() => {
    manager.send({ type: 'mark_all_read' });
  }, []);

  const isConnected = useSyncExternalStore(
    (cb) => {
      manager.statusListeners.add(cb);
      return () => manager.statusListeners.delete(cb);
    },
    () => manager.isOpen()
  );

  return { markAsRead, markAllAsRead, isConnected };
};

// ── Helpers (unchanged) ─────────────────────────────────────────────────────
const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification-sound.mp3');
    audio.volume = 0.3;
    audio.play().catch((err) => console.log('Could not play notification sound:', err));
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
};

const showBrowserNotification = (data) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(data.title || 'New Notification', {
      body: data.message,
      icon: '/logo.png',
      badge: '/logo.png',
      tag: data.id,
    });
  }
};

export const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return Notification.permission === 'granted';
};

export default useNotifications;